import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import PageShimmer from '../Shimmer/PageShimmer';
import { useClient } from '../../contexts/ClientContext';
import { setWithExpiry, getWithExpiry } from '../../utils/ttlStorage';
import '../../pages/CustomPage/CustomWebpage.css';

const HOMEPAGE_TEMPLATE_KEY_PREFIX = 'homepage_template_';
const HOMEPAGE_TEMPLATE_TTL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Renders the custom home page template (when client.home slug is set).
 * pageType from client.home: "landing" = no header/footer (handled in App), "webpage" = with header/footer.
 */
const HomePageTemplate = ({ slug, pageType: pageTypeProp }) => {
  const { header_tab } = useClient();
  const iframeRef = useRef(null);

  const pageTypeFromTabs = React.useMemo(() => {
    try {
      if (!slug) return 'landing';
      let tabs = [];
      const raw = typeof header_tab === 'string' ? JSON.parse(header_tab) : header_tab;
      if (Array.isArray(raw)) tabs = raw;
      else if (raw && raw.sub_tabs) tabs = raw.sub_tabs;
      const page = tabs.find(t => t.slug === slug || (t.url && t.url.includes(`/p/${slug}`)));
      return page ? page.type : 'landing';
    } catch (e) {
      return 'landing';
    }
  }, [slug, header_tab]);

  const pageType = pageTypeProp === 'webpage' ? 'webpage' : (pageTypeFromTabs === 'webpage' ? 'webpage' : 'landing');

  const [htmlContent, setHtmlContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }

    const storageKey = `${HOMEPAGE_TEMPLATE_KEY_PREFIX}${slug}`;

    const loadAndRender = async () => {
      try {
        setLoading(true);
        setError(null);

        const cached = getWithExpiry(storageKey);
        if (cached != null && cached.trim() !== '') {
          setHtmlContent(cached);
          setLoading(false);
          return;
        }

        const response = await api.get(`/api/webpage/${slug}`);
        if (!response.ok) {
          setError('This page is not available');
          setLoading(false);
          return;
        }

        const html = await response.text();
        if (!html || html.trim() === '') {
          setError('This page is not available');
          setLoading(false);
          return;
        }

        try {
          setWithExpiry(storageKey, html, HOMEPAGE_TEMPLATE_TTL_MS);
        } catch (e) {
          console.warn('Failed to cache homepage template:', e);
        }
        setHtmlContent(html);
      } catch (err) {
        console.error('Error loading homepage template:', err);
        setError('This page is not available');
      } finally {
        setLoading(false);
      }
    };

    loadAndRender();
  }, [slug]);

  useEffect(() => {
    const iframe = iframeRef.current;
    return () => {
      if (iframe && iframe.resizeObserver) {
        iframe.resizeObserver.disconnect();
      }
    };
  }, []);

  const handleIframeLoad = (e) => {
    if (pageType === 'webpage') {
      const iframe = e.target;
      const adjustHeight = () => {
        if (iframe.contentWindow && iframe.contentWindow.document) {
          const height = iframe.contentWindow.document.body.scrollHeight;
          iframe.style.height = `${height}px`;
        }
      };
      adjustHeight();
      if (iframe.contentWindow) {
        if (iframe.resizeObserver) iframe.resizeObserver.disconnect();
        const resizeObserver = new ResizeObserver(() => {
          window.requestAnimationFrame(adjustHeight);
        });
        resizeObserver.observe(iframe.contentWindow.document.body);
        iframe.resizeObserver = resizeObserver;
      }
    }
  };

  if (loading) {
    return <PageShimmer variant="custom-page" />;
  }

  if (error) {
    return (
      <div className="custom-webpage-container">
        <div className="custom-webpage-error">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>Page Not Available</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!htmlContent) {
    return null;
  }

  return (
    <div className="custom-webpage-container" style={pageType === 'webpage' ? { height: 'auto', overflow: 'visible' } : {}}>
      <iframe
        ref={iframeRef}
        title="Home"
        srcDoc={htmlContent}
        onLoad={handleIframeLoad}
        scrolling={pageType === 'webpage' ? 'no' : 'yes'}
        style={{
          width: '100%',
          height: pageType === 'webpage' ? 'auto' : '100vh',
          border: 'none',
          display: 'block',
          overflow: 'hidden'
        }}
      />
    </div>
  );
};

export default HomePageTemplate;
